import React, { useRef, useEffect, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getCenter } from "geolib";
import { customColors } from "models/customColors";
import { useTheme } from "@mui/material";
import PickUpImage from "assets/img/pick-up-image.png";
import DropOffImage from "assets/img/drop-off-image.png";
import CarImage from "assets/img/car-icon.svg";
import { unixTimeToUSTime } from "utils";
import { useMapAuth, MAP_STYLE_URL } from "./useMapAuth";

const ZOOM_FACTOR_PADDING = 60;
const RELOAD_DELAY_IN_MS = 500;
const MARKER_SIZE = 32;

function createImageMarker(imageSrc, bgColor) {
  const el = document.createElement("div");
  el.style.width = `${MARKER_SIZE}px`;
  el.style.height = `${MARKER_SIZE}px`;
  el.style.backgroundImage = `url(${imageSrc})`;
  el.style.backgroundSize = "60%";
  el.style.backgroundRepeat = "no-repeat";
  el.style.backgroundPosition = "center";
  el.style.backgroundColor = bgColor;
  el.style.borderRadius = "50%";
  el.style.border = "2px solid white";
  el.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
  return el;
}

const defaultCoords = { lat: 29.749907, lon: -95.358421 };

export const RouteMapView = ({
  route = [],
  preRoute = [],
  departure,
  destination,
  driver = null,
}) => {
  const theme = useTheme();
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const authOptions = useMapAuth();
  const [isVisible, setIsVisible] = useState(true);

  const colors = customColors[theme.palette.mode];

  useEffect(() => {
    setIsVisible(false);
    const timeout = setTimeout(() => setIsVisible(true), RELOAD_DELAY_IN_MS);
    return () => clearTimeout(timeout);
  }, [route]);

  useEffect(() => {
    if (!authOptions || !mapContainer.current || !isVisible) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    markersRef.current = [];

    const allPoints = [...preRoute, ...route];
    if (!allPoints.length) allPoints.push(defaultCoords);

    const geoPoints = allPoints.map(({ lat, lon }) => ({
      latitude: lat,
      longitude: lon,
    }));
    const center = getCenter(geoPoints);

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: MAP_STYLE_URL,
      center: [center ? center.longitude : 0, center ? center.latitude : 0],
      zoom: 10,
      ...authOptions,
    });

    map.addControl(new maplibregl.NavigationControl(), "top-right");

    map.on("load", () => {
      const bounds = new maplibregl.LngLatBounds();

      if (route.length > 0) {
        const routeCoords = route.map(({ lat, lon }) => {
          bounds.extend([lon, lat]);
          return [lon, lat];
        });

        map.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "LineString", coordinates: routeCoords },
          },
        });

        map.addLayer({
          id: "route-line",
          type: "line",
          source: "route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": colors.textBody,
            "line-width": 5,
          },
        });

        const pickupEl = createImageMarker(PickUpImage, colors.new);
        const pickupPopup = new maplibregl.Popup({ offset: 25 }).setText(
          `Pick Up: ${departure || ""}`
        );
        const pickupMarker = new maplibregl.Marker({ element: pickupEl })
          .setLngLat([route[0].lon, route[0].lat])
          .setPopup(pickupPopup)
          .addTo(map);
        markersRef.current.push(pickupMarker);

        const dropoffEl = createImageMarker(DropOffImage, colors.new);
        const dropoffPopup = new maplibregl.Popup({ offset: 25 }).setText(
          `Drop Off: ${destination || ""}`
        );
        const dropoffMarker = new maplibregl.Marker({ element: dropoffEl })
          .setLngLat([
            route[route.length - 1].lon,
            route[route.length - 1].lat,
          ])
          .setPopup(dropoffPopup)
          .addTo(map);
        markersRef.current.push(dropoffMarker);
      }

      if (preRoute.length > 0) {
        const preRouteCoords = preRoute.map(({ lat, lon }) => {
          bounds.extend([lon, lat]);
          return [lon, lat];
        });

        map.addSource("pre-route", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "LineString", coordinates: preRouteCoords },
          },
        });

        map.addLayer({
          id: "pre-route-line",
          type: "line",
          source: "pre-route",
          layout: { "line-join": "round", "line-cap": "round" },
          paint: {
            "line-color": colors.enRoute,
            "line-width": 5,
          },
        });
      }

      const isStatusActive =
        !!driver?.status &&
        (driver.status === "EN_ROUTE" ||
          driver.status === "ARRIVED" ||
          driver.status === "ON_BOARD");

      if (isStatusActive && driver?.coordinates) {
        const carEl = createImageMarker(CarImage, colors.actionColor);
        const subtitle = driver?.time ? unixTimeToUSTime(driver.time) : "";
        const carPopup = new maplibregl.Popup({ offset: 25 }).setText(
          `${driver?.driverName || ""} ${subtitle}`
        );
        const carMarker = new maplibregl.Marker({ element: carEl })
          .setLngLat([driver.coordinates.lon, driver.coordinates.lat])
          .setPopup(carPopup)
          .addTo(map);
        markersRef.current.push(carMarker);
        bounds.extend([driver.coordinates.lon, driver.coordinates.lat]);
      }

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: ZOOM_FACTOR_PADDING });
      }
    });

    mapRef.current = map;

    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authOptions, route, preRoute, driver, isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={mapContainer}
      style={{ width: "100%", height: "100%", minHeight: 300 }}
    />
  );
};
